// application components
import { CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpBackend, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { inject, NgModule, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideServiceWorker } from '@angular/service-worker';

// bayclubs modules
import { AuditLogViewerModule } from '@bayclubs/audit-log-viewer';
import { DropDownModule } from '@bayclubs/drop-down';
import { BcEmployeeAuthEnvironment, BcEmployeeAuthFacade, BcEmployeeAuthInterceptor, BcEmployeeAuthModule, BcEmployeeAuthState, } from '@bayclubs/employee-auth';
import {
    ApplicationModelV2,
    ApplicationsService,
    BASE_PATH as EMPLOYEE_API_BASE_PATH,
} from '@bayclubs/employee-management-api';
import { BASE_PATH as GROUP_CLASSES_BASE_PATH, GroupClassesOfficeApiApiModule } from '@bayclubs/group-classes-employee-api';
import {
    AppVersionFooter,
    AuthService as HorizonAuthService,
    HorizonLoggingConfig,
    isDeployedRuntimeEnvironment,
    provideApplicationGroupsLoader,
    provideAuth,
    provideHorizonErrorHandling,
    provideHorizonErrorHandlingTesting,
    provideHorizonLogging,
    provideHorizonLoginFactory,
    provideHorizonUserFactory,
    provideMenubarLogoHref,
    providePrimeng,
    provideVersionCheck,
    Toast
} from '@bayclubs/horizon';
import { LoggingModule, provideObservability } from '@bayclubs/logging';
import { BASE_PATH as PROVIDERS_API_BASE_PATH } from '@bayclubs/providers-api';
import {
    PROVIDERS_FRONTEND_CONFIGURATION,
    ProvidersFrontendConfiguration,
    ProvidersPrivilegesProviderService,
} from '@bayclubs/providers-frontend';
import { BcRecurringModule } from '@bayclubs/recurring';
import { SpinnerModule } from '@bayclubs/spinner';
import { TimeService } from '@bayclubs/ui-core';
import { BayclubsUiTextMaskModule } from '@bayclubs/ui-text-mask';
import {
    AuthenticationService,
    LoaderInterceptor,
    ModalService,
    PRIVILEGES_PROVIDERS,
    TranslationService,
    UtilsModule,
} from '@bayclubs/utils';

import { TranslateLoader, TranslateModule } from '@ngx-translate/core';

// ngxs
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsFormPluginModule } from '@ngxs/form-plugin';
import { NgxsRouterPluginModule } from '@ngxs/router-plugin';
import { NgxsStoragePluginModule, StorageOption } from '@ngxs/storage-plugin';
import { NgxsModule } from '@ngxs/store';
import { PageHeaderComponent } from '@shared/components/page-header/page-header.component';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { provideNgxMask } from 'ngx-mask';
import { MultiTranslateHttpLoader } from 'ngx-translate-multi-http-loader';
import { environment } from '../environments/environment';

// application modules
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { DualAuthInterceptor } from './core/auth/dual-auth.interceptor';
import { buildProtectedResources } from './core/auth/protected-resources';
import { CoreModule } from './core/core.module';
import { AuthService } from './core/services/auth.service';
import { FeaturesModule } from './features/features.module';
import {
    GroupClassesActionsModule,
    ProviderPayratesHistoryProvider,
    ProviderPayratesProvider,
} from './group-classes-actions/group-classes-actions.module';
import { InfrastructureModule } from './infrastructure/infrastructure.module';
import { InterfacesModule } from '@interfaces/interfaces.module';
import { ManagementModule } from './management/management.module';
import { MenubarComponent } from './navigation/components/menubar/menubar.component';
import { ReportsModule } from './reports/reports.module';
import { SharedModule } from '@shared/shared.module';
import { SubBoardModule } from './sub-board/sub-board.module';

const legacyLoggingConfig = {
    seqLoggingApiKey: environment.seqLoggingApiKey,
    seqLoggingEndpoint: environment.seqLoggingEndpoint,
    consoleLogEnabled: false,
    appVersion: environment.appVersion,
    apiBaseUrl: environment.managementApiUrl
};

const horizonLoggingConfig: Partial<HorizonLoggingConfig> = {
    appVersion: environment.appVersion,
    console: {
        enabled: !environment.production
    },
    seq: {
        enabled: !!environment.seqLoggingEndpoint,
        endpoint: environment.seqLoggingEndpoint,
        apiKey: environment.seqLoggingApiKey
    }
};

@NgModule({
    declarations: [AppComponent],
    imports: [
        CommonModule,
        BrowserAnimationsModule,
        BrowserModule,
        AppRoutingModule,
        FormsModule,
        ReactiveFormsModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: createTranslateLoader,
                deps: [HttpBackend]
            }
        }),
        LoggingModule.forRoot({
            loggingConfig: legacyLoggingConfig
        }),
        TooltipModule.forRoot(),
        TabsModule.forRoot(),
        NgxsModule.forRoot([], { developmentMode: !environment.production }),
        NgxsRouterPluginModule.forRoot(),
        NgxsStoragePluginModule.forRoot({
            keys: [BcEmployeeAuthState],
            storage: StorageOption.LocalStorage
        }),
        NgxsReduxDevtoolsPluginModule.forRoot({ disabled: environment.production }),
        BcEmployeeAuthModule.forRoot({
            accessDeniedRoute: '/forbidden',
            loginRoute: '/login',
            adClientId: environment.adClientId,
            adTenant: environment.adTenant,
            authenticationBaseUrl: environment.authenticationBaseUrl,
            baseHref: environment.baseHref
        } as BcEmployeeAuthEnvironment),
        UtilsModule.forRoot(),
        SharedModule,
        InfrastructureModule.forRoot(),
        InterfacesModule,
        ManagementModule,
        SubBoardModule,
        ReportsModule,
        CoreModule,
        TypeaheadModule.forRoot(),
        SpinnerModule.forRoot(),
        DropDownModule.forRoot(),
        ModalModule.forRoot(),
        AuditLogViewerModule.forRoot({
            endpoint: environment.auditlogUrl
        }),
        BcRecurringModule,
        GroupClassesOfficeApiApiModule,
        BayclubsUiTextMaskModule,
        FeaturesModule,
        NgxsFormPluginModule.forRoot(),
        GroupClassesActionsModule,
        AppVersionFooter,
        MenubarComponent,
        PageHeaderComponent,
        Toast
    ],
    providers: [
        { provide: 'AuthEnvironment', useValue: environment },
        { provide: 'loggingConfig', useValue: legacyLoggingConfig },
        provideAppInitializer(() => {
           const initializerFn = (initTranslations)(inject(TranslationService));
           return initializerFn();
        }),
        provideHttpClient(withInterceptorsFromDi()),
        { provide: HTTP_INTERCEPTORS, useClass: DualAuthInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: LoaderInterceptor, multi: true },
        { provide: AuthenticationService, useClass: AuthService },
        { provide: EMPLOYEE_API_BASE_PATH, useValue: environment.employeeApiUrl },
        { provide: GROUP_CLASSES_BASE_PATH, useValue: (environment.managementApiUrl as string).replace('/api/1.0/', '') },
        { provide: 'ProvidersEnvironment', useValue: environment },
        {
            provide: PRIVILEGES_PROVIDERS,
            useClass: ProvidersPrivilegesProviderService,
            multi: true,
        },
        {
            provide: PROVIDERS_FRONTEND_CONFIGURATION,
            useFactory: () => {
                return {
                    gcManagementEnabled: true,
                    gcManagementBaseUrl: environment.managementApiUrl,
                    providerWorkRoleTypeCodesFilter: ['gcinstructor'],
                    showProviderWorkingHours: false,
                    showProviderExternalProviderActions: true,
                } as ProvidersFrontendConfiguration;
            },
        },
        {
            provide: PROVIDERS_API_BASE_PATH,
            useValue: (environment.providersApiUrl as string).replace('/api/1.0/', ''),
        },
        { provide: 'EXTERNAL_GROUP_CLASSES_PAYRATES', useClass: ProviderPayratesProvider, multi: false },
        { provide: 'EXTERNAL_GROUP_CLASSES_PAYRATES_HISTORY', useClass: ProviderPayratesHistoryProvider, multi: false },
        ModalService,
        AuthService,
        GroupClassesActionsModule,
        provideNgxMask(),
        TimeService,
        provideServiceWorker('ngsw-worker.js', {
            enabled: isDeployedRuntimeEnvironment()
        }),
        providePrimeng(),
        provideVersionCheck(),
        provideMenubarLogoHref(environment.officeAppUrl),
        provideApplicationGroupsLoader<ApplicationModelV2>({
            loaderFactory: () => {
                const api = inject(ApplicationsService);
                return () => api.api10ApplicationsV2Get();
            }
        }),
        provideHorizonLogging({
            config: horizonLoggingConfig
        }),
        // MSAL: no user factory - Horizon falls back to its own AuthService (MSAL account).
        ...(environment.isMsalAuthenticationEnabled
            ? []
            : [provideHorizonUserFactory((auth) => {
                const token = (auth as BcEmployeeAuthFacade).getTokenSnapshot();

                return token ? {
                    id: token.id,
                    name: token.name,
                    login: token.login,
                    email: token.upn
                } : null;
            }, [BcEmployeeAuthFacade])]),
        provideHorizonErrorHandling(),
        provideHorizonErrorHandlingTesting({
            enabled: !environment.production
        }),
        provideObservability(),
        // Legacy branch: DualAuthInterceptor resolves BcEmployeeAuthInterceptor from the injector,
        // and HTTP_INTERCEPTORS with useClass does not register the class as a token - keep it here.
        ...(environment.isMsalAuthenticationEnabled
            ? [provideAuth({
                config: {
                    clientId: environment.adClientId,
                    tenantId: environment.adTenant,
                    redirectUri: environment.adRedirectUri,
                    protectedResources: buildProtectedResources([
                        [environment.managementApiUrl, environment.managementApiScope],
                        [environment.subBoardApiUrl, environment.subBoardApiScope],
                        [environment.providersApiUrl, environment.providersApiScope],
                        [environment.employeeApiUrl, environment.employeeApiScope],
                        [environment.auditlogUrl, environment.auditlogApiScope],
                    ]),
                    loginFailedRoute: '/login',
                    returnUrlStrategy: 'remember',
                },
            })]
            : [BcEmployeeAuthInterceptor]),
        ...(environment.isMsalAuthenticationEnabled
            ? [provideHorizonLoginFactory((authService) => {
                const horizonAuth = authService as HorizonAuthService;
                return {
                    isAuthenticated: () => horizonAuth.isAuthenticated(),
                    login: () => horizonAuth.loginRedirect()
                };
            }, [HorizonAuthService])]
            : [provideHorizonLoginFactory((authFacade) => {
                const employeeAuth = authFacade as BcEmployeeAuthFacade;
                return {
                    isAuthenticated: () => employeeAuth.getIsAuthenticatedSnapshot(),
                    login: () => employeeAuth.login()
                };
            }, [BcEmployeeAuthFacade])])
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}

export function createTranslateLoader(http: HttpBackend) {
    return new MultiTranslateHttpLoader(http, [
        { prefix: 'localization/', suffix: '.json?timestamp=' + new Date().getTime() },
        { prefix: 'l10n/recurring/', suffix: '.json?timestamp=' + new Date().getTime() },
        { prefix: 'l10n/providers/', suffix: '.json?timestamp=' + new Date().getTime() },
    ]);

    // return new TranslateHttpLoader(http, 'localization/', '.json?timestamp=' + new Date().getTime());
}

export function initTranslations(translationService: TranslationService) {
    return translationService.initSynchronously('locale-en');
}
